import { useEffect } from "react";
import {
  Modal,
  Select,
  Button,
  SelectItem,
  ModalHeader,
  ModalContent,
  ModalBody,
  ModalFooter,
  addToast,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { PostMergeCustomers } from "@/server-types";
import useFeature from "@/hooks/useFeature";
import { useForm } from "@mantine/form";

type Customer = {
  name: string;
  lastName: string;
  gsm: string | null;
  email: string | null;
  notes: string | null;
  customerId: number;
};

const CustomerMergeModal = ({
  customers,
  isOpen,
  onOpenChange,
  onMerge,
}: {
  customers: Customer[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onMerge: (values: PostMergeCustomers["body"]) => void;
}) => {
  const { isFeatureEnabled } = useFeature();

  const getFirstValidValue = (
    customers: Customer[],
    key: keyof Customer,
  ): string => {
    const found = customers.find(
      (customer) =>
        customer[key] !== null && customer[key]!.toString().trim().length > 0,
    );
    return found ? found[key]!.toString() : "";
  };

  const form = useForm({
    initialValues: {
      gsm: "",
      name: "",
      lastName: "",
      email: "",
      notes: "",
    },
    validate: {
      name: (value) => (!value || value.length === 0 ? t`Izberi ime` : null),
      lastName: (value) =>
        !value || value.length === 0 ? t`Izberi priimek` : null,
    },
  });

  useEffect(() => {
    form.setValues({
      gsm: getFirstValidValue(customers, "gsm"),
      name: getFirstValidValue(customers, "name"),
      lastName: getFirstValidValue(customers, "lastName"),
      email: getFirstValidValue(customers, "email"),
      notes: getFirstValidValue(customers, "notes"),
    });
  }, [customers]);

  const generateSelectItems = (key: keyof Customer) => {
    return customers
      .filter(
        (customer) => customer[key] && customer[key]?.toString().trim() !== "",
      )
      .map((customer) => ({
        key: customer.customerId.toString(),
        label: customer[key]!.toString(),
        value: customer[key]!.toString(),
      }));
  };

  const handleSubmit = () => {
    const validation = form.validate();
    if (validation.hasErrors) {
      addToast({
        title: t`Napaka`,
        description: t`Izberi ime in priimek`,
        variant: "flat",
      });
      return;
    }

    onMerge({
      ...form.getValues(),
      customerIds: customers.map((customer) => Number(customer.customerId)),
    });
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <ModalContent>
        {(onClose) => (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <ModalHeader>{t`Podatki združenega uporabnika`}</ModalHeader>
            <ModalBody>
              <Select
                className="w-full"
                label={`${t`Telefonska številka`}:`}
                selectedKeys={form.values.gsm ? [form.values.gsm] : []}
                onChange={(e) => form.setFieldValue("gsm", e.target.value)}
                items={generateSelectItems("gsm")}
              >
                {(item) => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                )}
              </Select>

              <Select
                className="w-full"
                label={`${t`Ime`}:`}
                selectedKeys={form.values.name ? [form.values.name] : []}
                onChange={(e) => form.setFieldValue("name", e.target.value)}
                items={generateSelectItems("name")}
              >
                {(item) => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                )}
              </Select>

              <Select
                className="w-full"
                label={`${t`Priimek`}:`}
                selectedKeys={
                  form.values.lastName ? [form.values.lastName] : []
                }
                onChange={(e) => form.setFieldValue("lastName", e.target.value)}
                items={generateSelectItems("lastName")}
              >
                {(item) => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                )}
              </Select>

              <Select
                className="w-full"
                label={`${t`Email`}:`}
                selectedKeys={form.values.email ? [form.values.email] : []}
                onChange={(e) => form.setFieldValue("email", e.target.value)}
                items={generateSelectItems("email")}
              >
                {(item) => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                )}
              </Select>

              <Select
                className="w-full"
                label={`${t`Opombe`}:`}
                selectedKeys={form.values.notes ? [form.values.notes] : []}
                onChange={(e) => form.setFieldValue("notes", e.target.value)}
                items={generateSelectItems("notes")}
              >
                {(item) => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                )}
              </Select>

              {isFeatureEnabled("CUSTOMER_FILES") ? (
                <p className="text-sm font-semibold text-danger-500">
                  <Trans>
                    Datoteke strank se bodo prenesle na združeno stranko
                  </Trans>
                </p>
              ) : undefined}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onPress={onClose}>
                <Trans>Prekliči</Trans>
              </Button>
              <Button onPress={() => handleSubmit()}>
                <Trans>Združi</Trans>
              </Button>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CustomerMergeModal;
